import { ChangeEvent, RefObject } from "react";
import { ImageIcon, Paperclip, Send, Video, X } from "lucide-react";

import { cn } from "@/app/utils/cn";
import { Textarea } from "@/view/components/ui/textarea";

import { formatFileSize } from "./useSupportRequestDetailSheet";

interface CommentBoxProps {
  message: string;
  setMessage: (value: string) => void;
  files: File[];
  fileInputRef: RefObject<HTMLInputElement>;
  onSelectFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onOpenFilePicker: () => void;
  onSend: () => void;
  allowAttachments: boolean;
  canSubmit: boolean;
  isSending: boolean;
  showSendButton: boolean;
}

export const CommentBox = ({
  message,
  setMessage,
  files,
  fileInputRef,
  onSelectFiles,
  onRemoveFile,
  onOpenFilePicker,
  onSend,
  allowAttachments,
  canSubmit,
  isSending,
  showSendButton,
}: CommentBoxProps) => {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3">
      <Textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        placeholder="Escreva um comentário…"
        className="min-h-[60px] resize-none border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
        disabled={isSending}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onSelectFiles}
      />

      {files.map((file, index) => (
        <div
          key={`${file.name}-${index}`}
          className="flex items-center gap-2 rounded-lg border border-gray-300 p-2"
        >
          {file.type.startsWith("video/") ? (
            <Video className="h-4 w-4 shrink-0" />
          ) : (
            <ImageIcon className="h-4 w-4 shrink-0" />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-xs text-gray-700">{file.name}</span>
            <span className="text-[10px] text-black04">
              {formatFileSize(file.size)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onRemoveFile(index)}
            disabled={isSending}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      {(allowAttachments || showSendButton) && (
        <div
          className={cn(
            "flex items-center justify-between gap-3",
            !allowAttachments && "justify-end",
          )}
        >
          {allowAttachments && (
            <button
              type="button"
              onClick={onOpenFilePicker}
              disabled={isSending}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-black03"
            >
              <Paperclip className="h-4 w-4" />
              Anexar
            </button>
          )}

          {showSendButton && (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSubmit}
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-primary",
                !canSubmit && "opacity-50",
              )}
            >
              {isSending ? "Enviando…" : "Enviar"}
              <Send className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
